import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  useTheme,
  alpha,
  styled,
  Tooltip,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import MicIcon from '@mui/icons-material/Mic';
// import MicOffIcon from '@mui/icons-material/MicOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
// import ScienceIcon from '@mui/icons-material/Science';
import { motion, AnimatePresence } from 'framer-motion';
import { transcribeAudio, validateAudioFile } from 'services/transcriptionService';
import { useDispatch } from 'react-redux';
import { addChatContext } from 'store/reducers/chatContext';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import config from 'config';

// Styled components with modern design
const StyledChatContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: theme.spacing(2)
}));

const StyledInputPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.02)})`,
  backdropFilter: 'blur(20px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
    transform: 'translateY(-2px)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
  },
  '&:focus-within': {
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}, 0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
    transform: 'translateY(-4px)',
    border: `1px solid ${theme.palette.primary.main}`
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    border: 'none',
    borderRadius: '20px',
    paddingRight: '20px',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.5,
    minHeight: '48px',
    boxShadow: 'none',
    outline: 'none',
    '& fieldset': {
      border: 'none'
    },
    '&:hover fieldset': {
      border: 'none'
    },
    '&.Mui-focused fieldset': {
      border: 'none'
    },
    '&.Mui-focused': {
      boxShadow: 'none',
      outline: 'none'
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    '&::placeholder': {
      color: '#9CA3AF', // Darker placeholder for better readability (Slate-400)
      opacity: 1
    }
  },
  '& textarea:focus': {
    outline: 'none'
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '12px 16px',
    maxHeight: '300px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.primary.main, 0.3),
      borderRadius: '3px'
    }
  }
}));

const ActionButton = styled(IconButton)(({ theme, variant }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'send':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`, // Green accent on hover
            boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.4)}`
          }
        };
      case 'accept':
        return {
          background: theme.palette.grey[200],
          color: theme.palette.success.main,
          '&:hover': {
            background: theme.palette.grey[300]
          }
        };
      case 'voice':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
          }
        };
      case 'cancel':
        return {
          background: theme.palette.grey[200],
          color: theme.palette.grey[600],
          '&:hover': {
            background: theme.palette.grey[300]
          }
        };
      case 'stop':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
          }
        };
      default:
        return {
          background: theme.palette.grey[200],
          color: theme.palette.grey[700],
          '&:hover': {
            background: theme.palette.grey[300],
            boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.1)}`
          }
        };
    }
  };

  return {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    margin: '0 4px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...getVariantStyles(),
    '&:hover': {
      transform: 'scale(1.1)',
      ...getVariantStyles()['&:hover']
    },
    '&:active': {
      transform: 'scale(0.95)'
    },
    '&.Mui-disabled, &:disabled': {
      opacity: 0.5,
      filter: 'grayscale(0.4)',
      boxShadow: 'none',
      transform: 'none'
    },
    '&.recording': {
      background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
      color: theme.palette.error.contrastText,
      animation: 'pulse 2s infinite'
    },
    '@keyframes pulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.7)}`
      },
      '70%': {
        boxShadow: `0 0 0 10px ${alpha(theme.palette.error.main, 0)}`
      },
      '100%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}`
      }
    }
  };
});

const SuggestionChip = styled(Chip)(({ theme }) => ({
  margin: '4px 6px 4px 0',
  borderRadius: '16px',
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.2),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
  }
}));

const TypingIndicator = ({ show }) => {
  const theme = useTheme();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              color: theme.palette.text.secondary,
              fontSize: '14px'
            }}
          >
            <SmartToyIcon sx={{ fontSize: '18px' }} />
            <span>AI is thinking</span>
            <Box sx={{ display: 'flex', gap: '2px', ml: 1 }}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main
                  }}
                />
              ))}
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AudioVisualization = ({ isRecording, audioLevels, height = 40, barWidth = 3, gap = '2px' }) => {
  const theme = useTheme();

  // Use real audio levels or fallback to low levels when not recording
  const levels = audioLevels.length > 0 ? audioLevels : Array(20).fill(0.1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: gap,
        height: height,
        px: 2
      }}
    >
      {levels.map((level, index) => (
        <motion.div
          key={index}
          animate={{
            scaleY: isRecording ? Math.max(0.2, Math.min(1, level * 1.5)) : 0.2,
            opacity: isRecording ? 0.6 + level * 0.4 : 0.3
          }}
          transition={{
            duration: 0.1,
            ease: 'easeOut'
          }}
          style={{
            width: barWidth,
            height: '100%',
            backgroundColor: isRecording
              ? level > 0.5
                ? theme.palette.error.main
                : theme.palette.primary.main
              : theme.palette.action.disabled,
            borderRadius: '1.5px',
            transformOrigin: 'bottom'
          }}
        />
      ))}
    </Box>
  );
};

const RecordingControlsCompact = ({
  voiceState,
  disabled,
  showStop,
  canSend,
  onVoiceToggle,
  onSend,
  onCancel,
  onAccept,
  onStop,
  audioLevels
}) => {
  const theme = useTheme();
  return (
    <AnimatePresence mode="wait">
      {voiceState === 'idle' && (
        <motion.div
          key="compact-ctrl-idle"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {!canSend && (
            <Tooltip title="Voice Input" arrow>
              <span>
                <ActionButton
                  onClick={onVoiceToggle}
                  disabled={disabled || showStop}
                  variant="voice"
                  sx={{ width: 28, height: 28, m: 0, mb: 0.5 }}
                >
                  <MicIcon sx={{ fontSize: 16 }} />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          {showStop ? (
            <Tooltip title="Stop" arrow>
              <span>
                <ActionButton onClick={onStop} disabled={disabled} variant="stop" sx={{ width: 28, height: 28, m: 0, mb: 0.5 }}>
                  <StopIcon sx={{ fontSize: 16 }} />
                </ActionButton>
              </span>
            </Tooltip>
          ) : canSend ? (
            <Tooltip title="Send Message" arrow>
              <span>
                <ActionButton onClick={onSend} disabled={!canSend || disabled} variant="send" sx={{ width: 28, height: 28, m: 0, mb: 0.5 }}>
                  <SendIcon sx={{ fontSize: 16 }} />
                </ActionButton>
              </span>
            </Tooltip>
          ) : null}
        </motion.div>
      )}

      {voiceState === 'recording' && (
        <motion.div
          key="compact-ctrl-recording"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            background: alpha(theme.palette.primary.main, 0.1),
            borderRadius: '18px',
            padding: '2px',
            marginRight: '2px'
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <AudioVisualization isRecording={true} audioLevels={audioLevels} height={28} barWidth={2} gap="1px" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <Tooltip title="Cancel" arrow>
              <span>
                <ActionButton onClick={onCancel} disabled={disabled} variant="cancel" sx={{ mx: 0.25, width: 28, height: 28 }}>
                  <CloseIcon fontSize="small" />
                </ActionButton>
              </span>
            </Tooltip>
            <Tooltip title="Accept" arrow>
              <span>
                <ActionButton onClick={onAccept} disabled={disabled} variant="accept" sx={{ mx: 0.25, width: 28, height: 28 }}>
                  <CheckIcon fontSize="small" />
                </ActionButton>
              </span>
            </Tooltip>
          </Box>
        </motion.div>
      )}

      {voiceState === 'transcribing' && (
        <motion.div
          key="compact-ctrl-transcribing"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            background: alpha(theme.palette.primary.main, 0.1),
            borderRadius: '22px',
            padding: '4px 8px',
            marginRight: '2px'
          }}
        >
          <CircularProgress size={12} color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
            Transcribing...
          </Typography>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PremiumChatInput = ({
  onSendMessage,
  placeholder = 'Ask me anything...',
  suggestions = ['How can I improve my product?', 'Analyze competitor features', 'Generate user personas'],
  disabled = false,
  showTyping = false,
  autoFocus = false,
  actionsPlacement = 'below', // 'below' | 'right'
  compact = false, // minimized view: one-line input, Send top-right
  disableInlineSuggestions = false,
  noTopPadding = false,
  noBottomPadding = false,
  dense = false,
  onStop, // optional; to be wired later
  showLeftPlus = false,
  externalValue,
  onChangeMessage,
  disableVoice = false,
  disableAttachments = false,
  minRowsOverride,
  allowEmptySend = false
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const auth = getAuth();
  const apiUrl = config.apiUrl;
  // Sidebar uses smaller threshold; fullscreen has wider input so increase threshold
  const CHAR_MULTILINE_THRESHOLD = compact ? 31 : 136;
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (typeof externalValue === 'string' && externalValue !== message) {
      setMessage(externalValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue]);
  // removed forced multiline; char/newline logic drives this now
  const [voiceState, setVoiceState] = useState('idle'); // 'idle', 'recording', 'transcribing'
  const [isSending, setIsSending] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [audioLevels, setAudioLevels] = useState([]);

  const textFieldRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const [plusAnchorEl, setPlusAnchorEl] = useState(null);
  // Tagging menu state
  const [tagAnchorEl, setTagAnchorEl] = useState(null);
  const [tagStep, setTagStep] = useState('root'); // 'root' | 'lp' | 'comp' | 'reviews' | 'icp'
  const [lpItems, setLpItems] = useState([]);
  const [lpLoading, setLpLoading] = useState(false);
  const [lpError, setLpError] = useState('');
  const [tagTriggerIndex, setTagTriggerIndex] = useState(null);
  // Competitors history state
  const [compItems, setCompItems] = useState([]);
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState('');
  // Reviews history state
  const [revItems, setRevItems] = useState([]);
  const [revLoading, setRevLoading] = useState(false);
  const [revError, setRevError] = useState('');
  // ICP history state
  const [icpItems, setIcpItems] = useState([]);
  const [icpLoading, setIcpLoading] = useState(false);
  const [icpError, setIcpError] = useState('');
  // removed tagChips visual inside input; tags will be shown near context toolbar instead

  useEffect(() => {
    if (autoFocus && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [autoFocus]);

  // Listen for external requests to insert text into the chat input (e.g., from ReportViewer selection)
  useEffect(() => {
    const onInsert = (e) => {
      try {
        const text = e.detail && e.detail.text ? String(e.detail.text) : '';
        if (!text || !text.trim()) return;
        setMessage((prev) => {
          const next = prev && prev.trim().length > 0 ? `${prev}\n${text}` : text;
          if (typeof onChangeMessage === 'function') onChangeMessage(next);
          return next;
        });
        // Focus the input so the user sees the inserted text
        if (textFieldRef.current && typeof textFieldRef.current.focus === 'function') {
          textFieldRef.current.focus();
        }
      } catch (_) {
        // ignore
      }
    };
    window.addEventListener('chat:insert_text', onInsert);
    return () => window.removeEventListener('chat:insert_text', onInsert);
  }, []);

  // Track whether the input is currently multiline based solely on char count
  const updateMultilineState = () => {
    const hasNewline = message.includes('\n');
    const byChar = message.length >= CHAR_MULTILINE_THRESHOLD;
    setIsMultiline(hasNewline || byChar);
  };
  useEffect(() => {
    updateMultilineState();
  }, [message]);
  useEffect(() => {
    setTimeout(updateMultilineState, 0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && voiceState === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [voiceState]);

  const handleSend = async () => {
    if (!disabled && !isSending && (allowEmptySend || message.trim())) {
      setIsSending(true);
      setShowSuggestions(false);

      try {
        const payload = allowEmptySend ? message : message.trim();
        await onSendMessage?.(payload);
        setMessage('');
        if (typeof onChangeMessage === 'function') onChangeMessage('');
        // no-op; char/newline logic
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // Set up audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      // Configure analyser for real-time volume detection
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDataArray = new Uint8Array(analyser.fftSize);

      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start audio level monitoring for volume-based visualization
      const updateAudioLevels = () => {
        if (mediaRecorderRef.current?.state === 'recording' && analyserRef.current) {
          // Get time-domain data for volume calculation
          analyserRef.current.getByteTimeDomainData(timeDataArray);

          // Calculate RMS (Root Mean Square) for overall volume
          let sum = 0;
          for (let i = 0; i < timeDataArray.length; i++) {
            const value = (timeDataArray[i] - 128) / 128; // Convert to -1 to 1 range
            sum += value * value;
          }
          const rms = Math.sqrt(sum / timeDataArray.length);
          const volume = Math.min(1, rms * 3); // Amplify for better visualization

          // Get frequency data for bar variation
          analyserRef.current.getByteFrequencyData(dataArray);

          // Create 20 bars with volume-based heights
          const barCount = 20;
          const levels = [];

          for (let i = 0; i < barCount; i++) {
            // Use frequency data for variation between bars
            const freqIndex = Math.floor((i / barCount) * bufferLength);
            const frequencyValue = dataArray[freqIndex] / 255;

            // Combine overall volume with frequency-specific data
            const baseLevel = volume * 0.7; // 70% from overall volume
            const freqVariation = frequencyValue * 0.3; // 30% from frequency data
            const combinedLevel = baseLevel + freqVariation;

            // Add slight random variation for more natural look
            const randomFactor = 0.9 + Math.random() * 0.2;
            levels.push(Math.min(1, combinedLevel * randomFactor));
          }

          setAudioLevels(levels);
          //   console.log('Audio Levels:', levels);
          animationFrameRef.current = requestAnimationFrame(updateAudioLevels);
        }
      };

      // Clear previous audio chunks
      audioChunksRef.current = [];

      // Create MediaRecorder with optimal settings for Whisper
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());

        // Clean up audio analysis
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }

        setAudioLevels([]);
      };

      mediaRecorder.start();
      setVoiceState('recording');
      updateAudioLevels();
      setError('');
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Unable to access microphone. Please check permissions.');
      setShowError(true);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop(); // This triggers onstop for cleanup
    }

    audioChunksRef.current = [];
    setVoiceState('idle');
    setAudioLevels([]);
  };

  const acceptRecording = async () => {
    // Stop recording first
    if (mediaRecorderRef.current && voiceState === 'recording') {
      mediaRecorderRef.current.stop();
    }

    // Start transcription
    setVoiceState('transcribing');

    // Wait a bit for the recording to finish processing
    setTimeout(async () => {
      await handleTranscription();
    }, 100);
  };

  const handleTranscription = async () => {
    if (audioChunksRef.current.length === 0) {
      setError('No audio recorded');
      setShowError(true);
      setVoiceState('idle');
      return;
    }

    try {
      // Create audio blob from recorded chunks
      const audioBlob = new Blob(audioChunksRef.current, {
        type: 'audio/webm'
      });

      // Create a File object for the transcription service
      const audioFile = new File([audioBlob], 'recording.webm', {
        type: 'audio/webm'
      });

      // Validate the audio file
      const validation = validateAudioFile(audioFile);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Transcribe the audio
      const result = await transcribeAudio({
        audioFile,
        language: null, // Auto-detect language
        prompt: 'Transcribe this voice message for a business chat interface.'
      });

      if (result.success && result.transcription) {
        // Append the transcribed text to the existing message
        setMessage((prev) => {
          const base = String(prev || '');
          if (!base) return result.transcription;
          const needsSpace = !(base.endsWith(' ') || base.endsWith('\n'));
          return base + (needsSpace ? ' ' : '') + result.transcription;
        });
        setShowSuggestions(false);
        textFieldRef.current?.focus();
      } else {
        throw new Error('Transcription failed');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setError(error.message || 'Failed to transcribe audio');
      setShowError(true);
    } finally {
      setVoiceState('idle');
      audioChunksRef.current = [];
    }
  };

  const handleVoiceToggle = () => {
    if (voiceState === 'idle') {
      startRecording();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    if (typeof onChangeMessage === 'function') onChangeMessage(suggestion);
    setShowSuggestions(false);
    textFieldRef.current?.focus();
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Handle file upload here
      console.log('File selected:', file.name);
    }
  };

  const openPlusMenu = (event) => {
    setPlusAnchorEl(event.currentTarget);
  };
  const closePlusMenu = () => setPlusAnchorEl(null);

  const openTagMenu = (event, triggerIndex = null) => {
    setTagAnchorEl(event.currentTarget);
    setTagStep('root');
    setTagTriggerIndex(triggerIndex);
  };
  const closeTagMenu = () => {
    setTagAnchorEl(null);
    setTagStep('root');
    setLpItems([]);
    setLpLoading(false);
    setLpError('');
    setTagTriggerIndex(null);
    setCompItems([]);
    setCompLoading(false);
    setCompError('');
    setRevItems([]);
    setRevLoading(false);
    setRevError('');
    setIcpItems([]);
    setIcpLoading(false);
    setIcpError('');
  };

  const loadLpHistory = async () => {
    setLpLoading(true);
    setLpError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/lpcompare/get_analysis_history/`, {
        params: { page: 1, page_size: 10 },
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data?.results) ? response.data.results : [];
      setLpItems(items);
    } catch (e) {
      setLpError('Failed to load recent LP analyses');
    } finally {
      setLpLoading(false);
    }
  };

  const formatCompListLabel = (urlListString) => {
    try {
      const arr = JSON.parse((urlListString || '').replace(/'/g, '"'));
      if (!Array.isArray(arr) || arr.length < 2) return 'Competitors Analysis';
      const [a, b, ...rest] = arr.map((u) =>
        String(u)
          .replace(/^(https?:\/\/)(www\.)?/, '')
          .replace(/\/$/, '')
      );
      const more = rest.length ? ` and ${rest.length} more` : '';
      return `${a} vs ${b}${more}`;
    } catch {
      return 'Competitors Analysis';
    }
  };

  const loadCompHistory = async () => {
    setCompLoading(true);
    setCompError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/lpcompare/get_comp_analysis_history/`, {
        params: { page: 1, page_size: 10 },
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data?.results) ? response.data.results : [];
      // include only ready items (not processing, success or enough subtasks)
      const ready = items.filter(
        (it) =>
          !it.processing_status &&
          (it.success_status === true ||
            (Array.isArray(it.subtasks_success_status) && it.subtasks_success_status.filter(Boolean).length >= 2))
      );
      setCompItems(ready);
    } catch (e) {
      setCompError('Failed to load competitors analyses');
    } finally {
      setCompLoading(false);
    }
  };

  const loadReviewsHistory = async () => {
    setRevLoading(true);
    setRevError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/analysis_projects/get_reviews_analysis_history/`, {
        params: { page: 1, page_size: 10 },
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data?.results) ? response.data.results : [];
      setRevItems(items);
    } catch (e) {
      setRevError('Failed to load reviews analyses');
    } finally {
      setRevLoading(false);
    }
  };

  const loadIcpHistory = async () => {
    setIcpLoading(true);
    setIcpError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/lpcompare/icp-history/`, {
        params: { page: 1, page_size: 10 },
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data?.results) ? response.data.results : [];
      setIcpItems(items);
    } catch (e) {
      setIcpError('Failed to load ICP analyses');
    } finally {
      setIcpLoading(false);
    }
  };

  const addLpReportToContext = async (taskId) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const res = await axios.get(`${apiUrl}/lpcompare/history/${taskId}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const report = res.data;
      const text = JSON.stringify(report);
      const picked = lpItems.find((i) => i.task_id === taskId);
      const label = picked?.url || String(taskId);
      const href = /^https?:\/\//i.test(label) ? label : null;
      dispatch(addChatContext(text, null, 'report', null, label, href, 'LP Analysis'));
      // Small visual hint
      setShowSuggestions(false);
      // Remove the '@' that triggered the menu (after successful selection)
      setMessage((prev) => {
        if (tagTriggerIndex == null) return prev;
        // Remove '@' at original position if still present
        if (prev[tagTriggerIndex] === '@') return prev.slice(0, tagTriggerIndex) + prev.slice(tagTriggerIndex + 1);
        // If cursor moved, remove first standalone '@' from the left of trigger index
        const left = prev.lastIndexOf('@', tagTriggerIndex);
        if (left !== -1) return prev.slice(0, left) + prev.slice(left + 1);
        if (prev.endsWith('@')) return prev.slice(0, -1);
        return prev;
      });
    } catch (e) {
      setError('Failed to add LP report to context');
      setShowError(true);
    } finally {
      closeTagMenu();
    }
  };

  const addCompReportToContext = async (taskId, item) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const res = await axios.get(`${apiUrl}/lpcompare/get_old_comp_analysis/${taskId}`, {
        params: { use_v2: true },
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const report = res.data;
      const text = JSON.stringify(report);
      const label = formatCompListLabel(item?.url_list);
      dispatch(addChatContext(text, null, 'report', null, label, null, 'Competitors Analysis'));
      setShowSuggestions(false);
      setMessage((prev) => {
        if (tagTriggerIndex == null) return prev;
        if (prev[tagTriggerIndex] === '@') return prev.slice(0, tagTriggerIndex) + prev.slice(tagTriggerIndex + 1);
        const left = prev.lastIndexOf('@', tagTriggerIndex);
        if (left !== -1) return prev.slice(0, left) + prev.slice(left + 1);
        if (prev.endsWith('@')) return prev.slice(0, -1);
        return prev;
      });
    } catch (e) {
      setError('Failed to add Competitors report to context');
      setShowError(true);
    } finally {
      closeTagMenu();
    }
  };

  const addReviewsReportToContext = async (taskId, item) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const res = await axios.get(`${apiUrl}/analysis_projects/get_reviews_history_request_results/${taskId}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const report = res.data;
      const text = JSON.stringify(report);
      const label = item?.url || String(taskId);
      const href = item?.url || null;
      dispatch(addChatContext(text, null, 'report', null, label, href, 'Reviews Analysis'));
      setShowSuggestions(false);
      setMessage((prev) => {
        if (tagTriggerIndex == null) return prev;
        if (prev[tagTriggerIndex] === '@') return prev.slice(0, tagTriggerIndex) + prev.slice(tagTriggerIndex + 1);
        const left = prev.lastIndexOf('@', tagTriggerIndex);
        if (left !== -1) return prev.slice(0, left) + prev.slice(left + 1);
        if (prev.endsWith('@')) return prev.slice(0, -1);
        return prev;
      });
    } catch (e) {
      setError('Failed to add Reviews report to context');
      setShowError(true);
    } finally {
      closeTagMenu();
    }
  };

  const addIcpReportToContext = async (taskId, item) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Unauthorized');
      const token = await user.getIdToken();
      const res = await axios.get(`${apiUrl}/lpcompare/icp-history/${taskId}/`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const report = res.data;
      const text = JSON.stringify(report);
      const label = item?.title || item?.name || `ICP #${taskId}`;
      dispatch(addChatContext(text, null, 'report', null, label, null, 'ICP Analysis'));
      setShowSuggestions(false);
      setMessage((prev) => {
        if (tagTriggerIndex == null) return prev;
        if (prev[tagTriggerIndex] === '@') return prev.slice(0, tagTriggerIndex) + prev.slice(tagTriggerIndex + 1);
        const left = prev.lastIndexOf('@', tagTriggerIndex);
        if (left !== -1) return prev.slice(0, left) + prev.slice(left + 1);
        if (prev.endsWith('@')) return prev.slice(0, -1);
        return prev;
      });
    } catch (e) {
      setError('Failed to add ICP report to context');
      setShowError(true);
    } finally {
      closeTagMenu();
    }
  };

  // Purely char-count driven
  useEffect(() => {
    updateMultilineState();
  }, [message]);

  return (
    <StyledChatContainer sx={{ ...(compact ? { p: 0 } : {}), ...(noTopPadding ? { pt: 0 } : {}), ...(noBottomPadding ? { pb: 0 } : {}) }}>
      {/* Suggestions */}
      <Box
        sx={{
          minHeight: showSuggestions && suggestions.length > 0 && !message && !disableInlineSuggestions ? 'auto' : 0,
          overflow: 'hidden'
        }}
      >
        <AnimatePresence mode="wait">
          {showSuggestions && suggestions.length > 0 && !message && !disableInlineSuggestions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <Box sx={{ mb: 2, textAlign: 'center', py: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                  {suggestions.map((suggestion, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <SuggestionChip label={suggestion} onClick={() => handleSuggestionClick(suggestion)} clickable />
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Typing Indicator - hidden when inline thinking is rendered in chat bubble */}
      <TypingIndicator show={showTyping && false} />

      {/* Main Input (ChatGPT-like layout) */}
      <StyledInputPaper
        elevation={0}
        sx={compact ? { borderRadius: '20px', '&:hover': { transform: 'none' }, '&:focus-within': { transform: 'none' } } : undefined}
      >
        <Box
          sx={{
            position: 'relative',
            ...(compact
              ? {}
              : {
                  paddingLeft: showLeftPlus && !disableAttachments ? 4 : 0,
                  paddingRight: actionsPlacement === 'right' ? 9 : 0
                })
          }}
        >
          {/* Tag chips moved to context toolbar; input remains clear when tagging */}

          <StyledTextField
            ref={textFieldRef}
            inputRef={textareaRef}
            fullWidth
            multiline
            minRows={typeof minRowsOverride === 'number' ? minRowsOverride : 1}
            maxRows={10}
            value={message}
            onChange={(e) => {
              const next = e.target.value;
              setMessage(next);
              if (typeof onChangeMessage === 'function') onChangeMessage(next);
            }}
            onKeyPress={handleKeyPress}
            onKeyDown={(e) => {
              if (e.key === '@') {
                const el = e.currentTarget;
                const start = el.selectionStart ?? message.length;
                const end = el.selectionEnd ?? start;
                // Only trigger tagging when cursor is not selecting text,
                // and the character before is start-of-text or whitespace.
                const prevChar = start > 0 ? message[start - 1] : null;
                const allowTrigger = start === end && (start === 0 || prevChar === ' ' || prevChar === '\n' || prevChar === '\t');
                if (allowTrigger) {
                  // Let '@' appear in input and open the tagging menu
                  setTimeout(() => openTagMenu({ currentTarget: el }, start), 0);
                }
                // else: do nothing (let the '@' be typed, e.g., emails like a@b.com)
              }
            }}
            placeholder={placeholder}
            disabled={disabled || isSending}
            variant="outlined"
            size={compact ? 'small' : 'medium'}
            sx={{
              ...(compact
                ? {
                    '& .MuiOutlinedInput-root': {
                      minHeight: '36px',
                      paddingRight: isMultiline ? '12px' : '84px',
                      paddingLeft: isMultiline ? '12px' : '44px'
                    },
                    '& .MuiOutlinedInput-input, & .MuiInputBase-inputMultiline': {
                      padding: '6px 10px'
                    }
                  }
                : {}),
              ...(dense
                ? {
                    '& .MuiOutlinedInput-root': {
                      minHeight: '40px'
                    },
                    '& .MuiOutlinedInput-input, & .MuiInputBase-inputMultiline': {
                      padding: '8px 12px'
                    }
                  }
                : {})
            }}
          />

          {/* Tagging menu (triggered by @) */}
          <Menu
            anchorEl={tagAnchorEl}
            open={Boolean(tagAnchorEl)}
            onClose={closeTagMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            // Ensure the Menu and Paper inherit a very high z-index in fullscreen
            sx={{ zIndex: 1500 }}
            disablePortal={false}
            slotProps={{ paper: { elevation: 3, sx: { zIndex: 'inherit' } } }}
            ModalProps={{ keepMounted: true }}
          >
            {tagStep === 'root' && [
              <MenuItem
                key="lp-root"
                onClick={() => {
                  setTagStep('lp');
                  loadLpHistory();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="LP Analysis" secondary="Insert a recent report as context" />
              </MenuItem>,
              <MenuItem
                key="comp-root"
                onClick={() => {
                  setTagStep('comp');
                  loadCompHistory();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Competitors Analysis" secondary="Pick from recent completed tasks" />
              </MenuItem>,
              <MenuItem
                key="reviews-root"
                onClick={() => {
                  setTagStep('reviews');
                  loadReviewsHistory();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Reviews Analysis" secondary="Pick from history" />
              </MenuItem>,
              <MenuItem
                key="icp-root"
                onClick={() => {
                  setTagStep('icp');
                  loadIcpHistory();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="ICP Analysis" secondary="Pick from history" />
              </MenuItem>
            ]}
            {tagStep === 'lp' && (
              <Box sx={{ minWidth: 320, maxWidth: 420 }}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Recent LP Analyses
                  </Typography>
                </Box>
                {lpLoading && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {lpError && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="error">
                      {lpError}
                    </Typography>
                  </Box>
                )}
                {!lpLoading &&
                  !lpError &&
                  lpItems.map((item) => (
                    <MenuItem key={item.task_id} onClick={() => addLpReportToContext(item.task_id)}>
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={item.url}
                        secondary={item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                      />
                    </MenuItem>
                  ))}
              </Box>
            )}
            {tagStep === 'comp' && (
              <Box sx={{ minWidth: 360, maxWidth: 520 }}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Recent Competitors Analyses
                  </Typography>
                </Box>
                {compLoading && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {compError && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="error">
                      {compError}
                    </Typography>
                  </Box>
                )}
                {!compLoading &&
                  !compError &&
                  compItems.map((item) => (
                    <MenuItem key={item.task_id} onClick={() => addCompReportToContext(item.task_id, item)}>
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={formatCompListLabel(item.url_list)}
                        secondary={item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                      />
                    </MenuItem>
                  ))}
              </Box>
            )}
            {tagStep === 'reviews' && (
              <Box sx={{ minWidth: 360, maxWidth: 520 }}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Recent Reviews Analyses
                  </Typography>
                </Box>
                {revLoading && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {revError && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="error">
                      {revError}
                    </Typography>
                  </Box>
                )}
                {!revLoading &&
                  !revError &&
                  revItems.map((item) => (
                    <MenuItem key={item.task_id} onClick={() => addReviewsReportToContext(item.task_id, item)}>
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={item.url}
                        secondary={item.updated_at ? new Date(item.updated_at).toLocaleString() : ''}
                      />
                    </MenuItem>
                  ))}
              </Box>
            )}
            {tagStep === 'icp' && (
              <Box sx={{ minWidth: 360, maxWidth: 520 }}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Recent ICP Analyses
                  </Typography>
                </Box>
                {icpLoading && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {icpError && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="error">
                      {icpError}
                    </Typography>
                  </Box>
                )}
                {!icpLoading &&
                  !icpError &&
                  icpItems.map((item) => (
                    <MenuItem key={item.task_id} onClick={() => addIcpReportToContext(item.task_id, item)}>
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={item.title || item.name || `ICP #${item.task_id}`}
                        secondary={item.updated_at ? new Date(item.updated_at).toLocaleString() : ''}
                      />
                    </MenuItem>
                  ))}
              </Box>
            )}
          </Menu>

          {/* Single-line: left plus overlay inside input without affecting width */}
          {compact && !isMultiline && !disableAttachments && (
            <Box
              sx={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                pointerEvents: 'auto'
              }}
            >
              <Tooltip title="More" arrow>
                <ActionButton onClick={openPlusMenu} variant="default" sx={{ width: 30, height: 30, m: 0 }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </ActionButton>
              </Tooltip>
              <Menu
                anchorEl={plusAnchorEl}
                open={Boolean(plusAnchorEl)}
                onClose={closePlusMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
                slotProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 100 } } }}
              >
                <MenuItem
                  onClick={() => {
                    closePlusMenu();
                    handleFileUpload();
                  }}
                >
                  <ListItemIcon>
                    <AttachFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Add photos & files" />
                </MenuItem>
              </Menu>
            </Box>
          )}

          {/* Non-compact: optional left plus like sidebar */}
          {!compact && showLeftPlus && !disableAttachments && (
            <Box
              sx={{
                position: 'absolute',
                left: '8px',
                top: isMultiline ? 'auto' : '50%',
                bottom: isMultiline ? 8 : 'auto',
                transform: isMultiline ? 'none' : 'translateY(-50%)',
                zIndex: 2,
                pointerEvents: 'auto'
              }}
            >
              <Tooltip title="More" arrow>
                <ActionButton onClick={openPlusMenu} variant="default" sx={{ width: 36, height: 36, m: 0 }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </ActionButton>
              </Tooltip>
              <Menu
                anchorEl={plusAnchorEl}
                open={Boolean(plusAnchorEl)}
                onClose={closePlusMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
                slotProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 100 } } }}
              >
                <MenuItem
                  onClick={() => {
                    closePlusMenu();
                    handleFileUpload();
                  }}
                >
                  <ListItemIcon>
                    <AttachFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Add photos & files" />
                </MenuItem>
              </Menu>
            </Box>
          )}

          {/* Compact: Mic + Send cluster on the right for single line */}
          {compact && !isMultiline ? (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                zIndex: 2,
                pointerEvents: 'auto'
              }}
            >
              <RecordingControlsCompact
                voiceState={voiceState}
                disabled={disabled}
                showStop={isSending || showTyping}
                canSend={!!message.trim()}
                onVoiceToggle={handleVoiceToggle}
                onSend={handleSend}
                onCancel={cancelRecording}
                onAccept={acceptRecording}
                onStop={onStop}
                audioLevels={audioLevels}
              />
            </Box>
          ) : // Preserve existing non-compact behavior
          actionsPlacement === 'right' ? (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                top: isMultiline ? 'auto' : '50%',
                bottom: isMultiline ? 8 : 'auto',
                transform: isMultiline ? 'none' : 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <AnimatePresence mode="wait">
                {voiceState === 'idle' && (
                  <motion.div
                    key="voice-idle"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {!showLeftPlus && !disableAttachments && (
                      <Tooltip title="Attach File" arrow>
                        <span>
                          <ActionButton onClick={handleFileUpload} disabled={disabled || isSending || showTyping} variant="attach">
                            <AttachFileIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    )}

                    {isSending || showTyping ? (
                      <Tooltip title="Stop" arrow>
                        <span>
                          <ActionButton onClick={onStop} disabled={disabled} variant="stop">
                            <StopIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    ) : allowEmptySend || message.trim() ? (
                      <Tooltip title="Send Message" arrow>
                        <span>
                          <ActionButton onClick={handleSend} disabled={(!allowEmptySend && !message.trim()) || disabled} variant="send">
                            <SendIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    ) : !disableVoice ? (
                      <Tooltip title="Voice Input" arrow>
                        <span>
                          <ActionButton onClick={handleVoiceToggle} disabled={disabled || isSending || showTyping} variant="voice">
                            <MicIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </motion.div>
                )}

                {voiceState === 'recording' && (
                  <motion.div
                    key="voice-recording"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '22px',
                      padding: '4px',
                      marginRight: '4px'
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                      <AudioVisualization isRecording={true} audioLevels={audioLevels} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                      <Tooltip title="Cancel" arrow>
                        <span>
                          <ActionButton onClick={cancelRecording} disabled={disabled} variant="cancel" sx={{ mx: 0.5 }}>
                            <CloseIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Accept" arrow>
                        <span>
                          <ActionButton onClick={acceptRecording} disabled={disabled} variant="accept" sx={{ mx: 0.5 }}>
                            <CheckIcon fontSize="small" />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </motion.div>
                )}

                {voiceState === 'transcribing' && (
                  <motion.div
                    key="voice-transcribing"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '22px',
                      padding: '8px 16px',
                      marginRight: '4px'
                    }}
                  >
                    <CircularProgress size={16} color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="primary">
                      Transcribing...
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          ) : null}

          {/* Compact: controls inside the input as the last line when multiline */}
          {compact && isMultiline && (
            <Box sx={{ pt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 0.5 }}>
                <Box>
                  <Tooltip title="More" arrow>
                    <ActionButton onClick={openPlusMenu} variant="default" sx={{ width: 32, height: 32, m: 0, mb: 0.5 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </ActionButton>
                  </Tooltip>
                  <Menu
                    anchorEl={plusAnchorEl}
                    open={Boolean(plusAnchorEl)}
                    onClose={closePlusMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <MenuItem
                      onClick={() => {
                        closePlusMenu();
                        handleFileUpload();
                      }}
                    >
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add photos & files" />
                    </MenuItem>
                  </Menu>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RecordingControlsCompact
                    voiceState={voiceState}
                    disabled={disabled}
                    isSending={isSending}
                    canSend={!!message.trim()}
                    onVoiceToggle={handleVoiceToggle}
                    onSend={handleSend}
                    onCancel={cancelRecording}
                    onAccept={acceptRecording}
                    audioLevels={audioLevels}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </StyledInputPaper>

      {/* Compact extra controls moved into plus menu */}

      {/* Hidden file input */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} accept="*/*" />

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </StyledChatContainer>
  );
};

export default PremiumChatInput;
